import { useRef } from "react";

import { useTranslations } from "next-intl";
import { QRCodeCanvas } from "qrcode.react";

import { FormButton } from "@/components/form/FormButton";
import { Icon } from "@/components/icons/Icon";
import { docPageUrl } from "@/urls";
import { trackEvent } from "@/utils/trackEvent";

import type { DocumentModel } from "@/models/DocumentModel";

export function QRCodeContent({ doc }: { doc: DocumentModel }) {
  const t = useTranslations();
  const qrValue = docPageUrl({ docId: doc.id ?? "" }, { host: true });
  const logoSrc = "/corca-logo.png";
  const logoRatio = 0.2;

  const qrCanvasRef = useRef<HTMLCanvasElement>(null);
  const fgColor = "#000000";
  const bgColor = "#FFFFFF";
  const size = 300;
  const logoBgColor = "#FFFFFF";

  const downloadPNG = async () => {
    const canvas = qrCanvasRef.current;
    if (!canvas) return;

    // Helper: convert a canvas node to an Image you can draw
    const canvasToImage = (cnv: HTMLCanvasElement) =>
      new Promise<HTMLImageElement>((resolve) => {
        const img = new Image();
        img.src = cnv.toDataURL("image/png");
        img.onload = () => resolve(img);
      });

    // Helper: load the logo
    const loadImage = (src: string) =>
      new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous"; // safe even if same-origin
        img.src = src;
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`Logo failed to load: ${src}`));
      });

    try {
      const [qrImg, logoImg] = await Promise.all([canvasToImage(canvas), loadImage(logoSrc)]);

      // Build a composite at the QR's pixel size
      const out = document.createElement("canvas");
      out.width = canvas.width;
      out.height = canvas.height;
      const ctx = out.getContext("2d")!;
      ctx.drawImage(qrImg, 0, 0);

      // Centered logo size/pos in device pixels
      const logoPx = Math.round(out.width * logoRatio);
      const x = Math.round((out.width - logoPx) / 2);
      const y = Math.round((out.height - logoPx) / 2);

      // Optional rounded backing behind the logo for contrast
      if (logoBgColor) {
        const pad = Math.round(logoPx * 0.18);
        const w = logoPx + pad * 2;
        const h = logoPx + pad * 2;
        roundedRectPath(ctx, x - pad, y - pad, w, h, 0);
        ctx.fillStyle = logoBgColor;
        ctx.fill();
        ctx.save();
        ctx.clip(); // round the logo too
      }

      ctx.drawImage(logoImg, x, y, logoPx, logoPx);

      if (logoBgColor) ctx.restore();

      // Download
      const url = out.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url;
      const formattedTitle = (
        doc.title?.replace(/[^a-zA-Z0-9]/g, "-") ||
        doc.id ||
        ""
      ).toLowerCase();
      a.download = `corca-document-${formattedTitle}-qr-link.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      // Track QR code download
      if (doc.id) {
        trackEvent("share_qr_code_downloaded", { doc_id: doc.id });
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="relative rounded-6 w-full pt-[100%] bg-white">
        <div className="absolute top-0 left-0 bottom-0 right-0 m-5">
          <QRCodeCanvas
            ref={qrCanvasRef}
            value={qrValue}
            size={size}
            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
            level="H"
            bgColor={bgColor}
            fgColor={fgColor}
          />
          <div className="absolute top-1/2 left-1/2 w-1/5 h-1/5 p-2.5 transform -translate-x-1/2 -translate-y-1/2 bg-white">
            <div className="flex flex-1">
              <img src={logoSrc} alt="Corca Logo" className="w-full h-full" />
            </div>
          </div>
        </div>
      </div>
      <FormButton
        variant="accent"
        className="rounded-6 h-9 mt-2.5 text-[15px]"
        onClick={downloadPNG}
      >
        <Icon name="Download" className="mr-1" />
        {t("share.download")}
      </FormButton>
    </div>
  );
}

// Draw a rounded-rect path
function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}
